import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../components/custom/Header";
import { ApiKey, ApiUrl, SessionKey, WebUrl } from "../../util/Constant";
import { stringIsNullOrEmpty } from "../../util/Util";
import { showResponseMessage } from "../../redux/AppAction";
import ApiEngine from "../../util/ApiEngine";
import Giveaway from "./Giveaway";
import SurveyEventCard from "../../components/survey/SurveyEventCard";

const phaseOrder = { ongoing: 0, future: 1, complete: 2 };
const typeOrder = { giveaway: 0, survey: 1 };
const filters = [
    ["all", "GIVEAWAY_ALL"],
    ["ongoing", "GIVEAWAY_ONGOING"],
    ["future", "GIVEAWAY_FUTURE"],
    ["complete", "GIVEAWAY_COMPLETE"]
];

const Event = () => {
    const { t, i18n } = useTranslation();
    const _history = useHistory();
    const _dispatch = useDispatch();
    const isLoggedIn = useSelector((state) => state.authState.isLoggedIn);
    const [campaigns, setCampaigns] = useState([]);
    const [survey, setSurvey] = useState(null);
    const [surveyOpen, setSurveyOpen] = useState(false);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        loadGiveaway();
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setSurvey(null);
            setSurveyOpen(false);
            return;
        }
        let cancelled = false;
        ApiEngine.get(ApiUrl._API_GET_ACTIVE_SURVEY + "?languageCode=" + encodeURIComponent(i18n.language || "en")).then((responseJson) => {
            if (!cancelled && responseJson && responseJson[ApiKey._API_SUCCESS_KEY]) {
                const data = responseJson[ApiKey._API_DATA_KEY];
                setSurvey(data && data.hasSurvey ? data : null);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isLoggedIn, i18n.language]);

    const loadGiveaway = async () => {
        let languageCode = localStorage.getItem(SessionKey._LANGUAGE);
        if (stringIsNullOrEmpty(languageCode) || languageCode === "undefined") {
            languageCode = "en";
        }
        let responseJson = await ApiEngine.get(ApiUrl._API_GET_GIVEAWAY_LIST + "?languageCode=" + languageCode);
        if (responseJson[ApiKey._API_SUCCESS_KEY]) {
            setCampaigns(responseJson[ApiKey._API_DATA_KEY] || []);
        }
        else {
            _dispatch(showResponseMessage(false, responseJson[ApiKey._API_MESSAGE_KEY]));
        }
    };

    const handleSurveySubmitted = () => {
        setSurvey((previous) => (previous ? { ...previous, submitted: true } : previous));
    };

    const items = [
        ...campaigns.map((campaign, index) => ({
            key: "giveaway-" + campaign.id,
            type: "giveaway",
            phase: campaign.phase,
            order: index,
            data: campaign
        })),
        ...(survey && (!survey.submitted || surveyOpen) ? [{
            key: "survey",
            type: "survey",
            phase: "ongoing",
            order: 0,
            data: survey
        }] : [])
    ].sort((a, b) => {
        const phaseDiff = (phaseOrder[a.phase] ?? 9) - (phaseOrder[b.phase] ?? 9);
        if (phaseDiff !== 0) {
            return phaseDiff;
        }
        const typeDiff = (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9);
        return typeDiff !== 0 ? typeDiff : a.order - b.order;
    });
    const visible = items.filter((item) => filter === "all" || item.phase === filter);
    const hasCards = visible.some((item) => item.type !== "survey" || !item.data.submitted);
    const renderItem = (item) => {
        if (item.type === "survey") {
            return (
                <SurveyEventCard
                    key={item.key}
                    survey={item.data}
                    platform={window.ReactNativeWebView ? "M4D" : "Mobile"}
                    onSubmitted={handleSurveySubmitted}
                    onOpenChange={setSurveyOpen}
                />
            );
        }
        if (item.type === "giveaway") {
            return <Giveaway key={item.key} campaign={item.data} />;
        }
        return null;
    };

    return (
        <div className="event-page">
            <Header history={_history} url={WebUrl._URL_MAIN} title={t("EVENT")} hasBackButton={true} />
            <p className="gw-sub">{t("GIVEAWAY_EXPLORE_SUB")}</p>
            <div className="giveaway-page">
                <div className="gw-filters">
                    {filters.map(([value, label]) => (
                        <button
                            key={value}
                            type="button"
                            className={"gw-filter" + (filter === value ? " active" : "")}
                            onClick={() => setFilter(value)}
                        >
                            {t(label)}
                        </button>
                    ))}
                </div>
                {!hasCards && <div className="gw-empty">{t("GIVEAWAY_NONE")}</div>}
                {visible.map(renderItem)}
            </div>
        </div>
    );
};

export default Event;
